/**
 * The pictures of one visit, grouped the way a field form reads: one section per question, and
 * inside a question one row per item when the question was answered for several items.
 *
 * Everything else — store, date, contributor — is shared by the whole visit, so it belongs to the
 * header rather than to each picture.
 */

const clean = (value) => (typeof value === 'string' ? value.trim() : '');

const questionOf = (picture) => clean(picture.translatedquestion) || clean(picture.question);

const timeOf = (picture) => `${picture.createdat ?? ''}`.slice(11, 16);

/**
 * @param {Array<object>} pictures the visit, ordered by `createdat` then `id` as the API returns it
 * @returns {{
 *   sections: Array<{ question: string, pictures: object[], items: ?Array<{ item: string, pictures: object[] }> }>,
 *   questions: number,
 *   timesVary: boolean
 * }} `items` is null when the question carries at most one item: its pictures then form one grid,
 *   each captioned with its own item. Pictures without an item come last, under an empty `item`.
 */
export const groupVisit = (pictures = []) => {
  const sections = new Map();

  for (const picture of pictures) {
    const question = questionOf(picture);
    if (!sections.has(question)) sections.set(question, { question, pictures: [] });
    sections.get(question).pictures.push(picture);
  }

  const grouped = [...sections.values()].map((section) => {
    const byItem = new Map();
    for (const picture of section.pictures) {
      const item = clean(picture.item);
      if (!byItem.has(item)) byItem.set(item, []);
      byItem.get(item).push(picture);
    }

    const named = [...byItem.keys()].filter(Boolean);
    if (named.length <= 1) return { ...section, items: null };

    const items = named.map(item => ({ item, pictures: byItem.get(item) }));
    if (byItem.has('')) items.push({ item: '', pictures: byItem.get('') });
    return { ...section, items };
  });

  return {
    sections: grouped,
    questions: grouped.length,
    timesVary: new Set(pictures.map(timeOf)).size > 1,
  };
};

/**
 * Pictures of the visit in reading order — section by section, item by item — which is the order
 * the arrows walk once a picture of the visit is opened.
 *
 * @param {ReturnType<typeof groupVisit>} grouped
 */
export const readingOrder = (grouped) => grouped.sections.flatMap(section => (section.items
  ? section.items.flatMap(group => group.pictures)
  : section.pictures));
